import { createContext, useContext, ReactNode, useState, useCallback, useMemo } from 'react'
import { LoginModal } from '@/components/LoginModal'

interface LoginModalOptions {
  onSignupComplete?: () => void;
}

interface LoginModalContextType {
  showLoginModal: boolean
  openLoginModal: (options?: LoginModalOptions) => void
  closeLoginModal: () => void
  showSignupForm: boolean
  setShowSignupForm: (show: boolean) => void
  onSignupComplete?: () => void
  showReferralWelcomeModal: boolean
  setShowReferralWelcomeModal: (show: boolean) => void
}

const LoginModalContext = createContext<LoginModalContextType | undefined>(undefined)

export function LoginModalProvider({ children }: { children: ReactNode }) {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showSignupForm, setShowSignupForm] = useState(false)
  const [showReferralWelcomeModal, setShowReferralWelcomeModal] = useState(false)
  const [onSignupComplete, setOnSignupComplete] = useState<(() => void) | undefined>(undefined)

  const openLoginModal = useCallback((options?: LoginModalOptions) => {
    if (options?.onSignupComplete) {
      setOnSignupComplete(() => options.onSignupComplete)
    }
    setShowLoginModal(true)
  }, [])

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false)
    setShowSignupForm(false)
  }, [])

  const handleSignupComplete = useCallback(() => {
    if (onSignupComplete) {
      onSignupComplete()
      // 콜백 실행 후 초기화
      setOnSignupComplete(undefined)
    }
    closeLoginModal()
  }, [onSignupComplete, closeLoginModal])

  const value = useMemo(() => ({
    showLoginModal,
    openLoginModal,
    closeLoginModal,
    showSignupForm,
    setShowSignupForm,
    onSignupComplete: handleSignupComplete,
    showReferralWelcomeModal,
    setShowReferralWelcomeModal
  }), [
    showLoginModal,
    openLoginModal,
    closeLoginModal,
    showSignupForm,
    handleSignupComplete,
    showReferralWelcomeModal
  ])

  return (
    <LoginModalContext.Provider value={value}>
      {children}
      <LoginModal 
        open={showLoginModal} 
        onClose={closeLoginModal} 
        onSignupComplete={handleSignupComplete}
      />
    </LoginModalContext.Provider>
  )
}

export const useLoginModal = () => {
  const context = useContext(LoginModalContext)
  if (!context) throw new Error('useLoginModal must be used within LoginModalProvider')
  return context
} 